import {
  Box,
  FormControl,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { isNil } from "lodash-es";
import { useMemo, useState } from "react";

import { ArrayHelper } from "@/common/helpers/array";
import { ObjectHelper } from "@/common/helpers/object";
import { ApiEnumName } from "@/common/services/enum";
import {
  DamageCorrectionStatus,
  VehicleArea,
  VehicleDamageState,
  VehicleProjection,
} from "@/core/api/generated/v1";

import AppButton from "../../Button/AppButton";
import ApiEnumAutocomplete from "../../Enum/ApiEnumAutocomplete";
import ApiEnumsAutocomplete from "../../Enum/ApiEnumsAutocomplete";
import AppIcon from "../../Icons/AppIcon";
import AppPopoverActions from "../../Popover/AppPopoverActions";
import AppTooltip from "../../Tooltip/AppTooltip";
import DamageTypeAutocompleteV2 from "../ReferenceData/DamageType/DamageTypeAutocompleteV2";
import VehiclePartTypeAutocompleteV2 from "../ReferenceData/VehiclePartType/VehiclePartTypeAutocompleteV2";
import VehicleDamageLink from "./VehicleDamageLink";

/** Unset keys are considered as `false`. */
type DisplayProps = {
  [Key in keyof VehicleDamageAutocompleteCustomFiltersValues]: boolean;
};

const defaultDisplayProps: DisplayProps = {
  partTypeId: true,
  damageTypeId: true,
  area: true,
  projection: true,
  states: true,
  correctionStatus: true,
  suggestedIds: true,
  restrictedIds: true,
};

export interface VehicleDamageAutocompleteCustomFiltersValues {
  partTypeId?: string;
  damageTypeId?: string;
  area?: VehicleArea;
  projection?: VehicleProjection;
  states?: VehicleDamageState[];
  correctionStatus?: DamageCorrectionStatus;
  suggestedIds?: string[];
  restrictedIds?: string[];

  // memory for state preservation (internal use)
  _suggestedIdsMemory?: string[];
  _restrictedIdsMemory?: string[];
}

export interface VehicleDamageAutocompleteCustomFilters {
  values: Nil<VehicleDamageAutocompleteCustomFiltersValues>;
  count: number;
  isAny: boolean;
}

export interface VehicleDamageAutocompleteCustomFiltersInputProps {
  searchFilters: {
    vehicleId: string;
  };
  defaultFilters?: Partial<VehicleDamageAutocompleteCustomFiltersValues> | null;
  displayProps?: Partial<DisplayProps>;
  onSave: (newValue: VehicleDamageAutocompleteCustomFilters | null) => void;
  onCancel: () => void;
}

function isPrivateStateKey(key: keyof VehicleDamageAutocompleteCustomFiltersValues) {
  return key.startsWith("_");
}

function isResettableStateKey(key: keyof VehicleDamageAutocompleteCustomFiltersValues) {
  return !isPrivateStateKey(key) && key !== "restrictedIds";
}

export function computeCustomFilters(
  values: Nil<VehicleDamageAutocompleteCustomFiltersValues>,
): VehicleDamageAutocompleteCustomFilters {
  const count = values
    ? Object.entries(values).filter(
        ([key, value]) =>
          !isPrivateStateKey(key as keyof VehicleDamageAutocompleteCustomFiltersValues) &&
          !isNil(value),
      ).length
    : 0;
  return {
    values: values,
    count: count,
    isAny: count > 0,
  };
}

export function VehicleDamageAutocompleteCustomFiltersInput({
  searchFilters,
  defaultFilters,
  displayProps,
  onSave,
  onCancel,
}: VehicleDamageAutocompleteCustomFiltersInputProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const [values, setValues] = useState<VehicleDamageAutocompleteCustomFiltersValues | null>(
    defaultFilters
      ? {
          ...defaultFilters,
          _suggestedIdsMemory: defaultFilters._suggestedIdsMemory || defaultFilters.suggestedIds,
          _restrictedIdsMemory: defaultFilters._restrictedIdsMemory || defaultFilters.restrictedIds,
        }
      : null,
  );

  const filtersComputed = useMemo<VehicleDamageAutocompleteCustomFilters>(
    () => computeCustomFilters(values),
    [values],
  );

  const reset = () => {
    setValues(
      values
        ? ObjectHelper.resetValues(values, undefined, (key) => isResettableStateKey(key))
        : null,
    );
  };

  const save = () => {
    onSave(filtersComputed);
    reset();
  };

  const cancel = () => {
    onCancel();
    reset();
  };

  return (
    <Box>
      <Typography variant='subtitle1' sx={{ mb: 1 }}>
        Filters
      </Typography>

      <Box>
        {displayProps.partTypeId && (
          <FormControl margin='dense' fullWidth>
            <VehiclePartTypeAutocompleteV2
              entityId={values?.partTypeId}
              isPreload={false}
              size='small'
              onChange={(newValue) => {
                setValues((prev) => ({ ...prev, partTypeId: newValue?.id || undefined }));
              }}
            />
          </FormControl>
        )}

        {displayProps.damageTypeId && (
          <FormControl margin='dense' fullWidth>
            <DamageTypeAutocompleteV2
              entityId={values?.damageTypeId}
              isPreload={false}
              size='small'
              onChange={(newValue) => {
                setValues((prev) => ({ ...prev, damageTypeId: newValue?.id || undefined }));
              }}
            />
          </FormControl>
        )}

        {displayProps.area && (
          <FormControl margin='dense' fullWidth>
            <ApiEnumAutocomplete
              type={ApiEnumName.VehicleArea}
              value={values?.area}
              label='Area'
              size='small'
              onChange={(newValue) => {
                setValues((prev) => ({ ...prev, area: newValue || undefined }));
              }}
            />
          </FormControl>
        )}

        {displayProps.projection && (
          <FormControl margin='dense' fullWidth>
            <ApiEnumAutocomplete
              type={ApiEnumName.VehicleProjection}
              value={values?.projection}
              label='Projection'
              size='small'
              onChange={(newValue) => {
                setValues((prev) => ({ ...prev, projection: newValue || undefined }));
              }}
            />
          </FormControl>
        )}

        {displayProps.states && (
          <FormControl margin='dense' fullWidth>
            <ApiEnumsAutocomplete
              type={ApiEnumName.VehicleDamageState}
              values={values?.states}
              label='States'
              size='small'
              onChange={(newValue) => {
                setValues((prev) => ({ ...prev, states: newValue || undefined }));
              }}
            />
          </FormControl>
        )}

        {displayProps.correctionStatus && (
          <FormControl margin='dense' fullWidth>
            <ApiEnumAutocomplete
              type={ApiEnumName.DamageCorrectionStatus}
              value={values?.correctionStatus}
              label='Correction status'
              size='small'
              onChange={(newValue) => {
                setValues((prev) => ({ ...prev, correctionStatus: newValue || undefined }));
              }}
            />
          </FormControl>
        )}

        {displayProps.suggestedIds &&
          (ArrayHelper.isNotEmpty(values?.suggestedIds) ||
            ArrayHelper.isNotEmpty(values?._suggestedIdsMemory)) && (
            <FormControl margin='dense' fullWidth>
              <ToggleButtonGroup
                sx={{ mb: 1 }}
                color='standard'
                size='small'
                value={ArrayHelper.isEmpty(values?.suggestedIds) ? "all" : "restricted"}
                exclusive
                onChange={(e, value) => {
                  setValues((prev) => ({
                    ...prev,
                    suggestedIds: value === "all" ? undefined : prev?._suggestedIdsMemory,
                    _suggestedIdsMemory: prev?._suggestedIdsMemory || prev?.suggestedIds,
                  }));
                }}
              >
                <ToggleButton value='all'>All damages</ToggleButton>
                <ToggleButton value='restricted'>
                  Suggested damages
                  <AppTooltip title='Damages from the source operation or relevant to the current context.'>
                    <AppIcon sx={{ ml: 0.5 }} of='info' />
                  </AppTooltip>
                </ToggleButton>
              </ToggleButtonGroup>

              {ArrayHelper.isNotEmpty(values?.suggestedIds) && (
                <Stack direction='column' gap={0}>
                  {values?.suggestedIds?.map((id) => (
                    <VehicleDamageLink
                      key={id}
                      vehicleId={searchFilters.vehicleId}
                      entityId={id}
                      entity={undefined}
                    />
                  ))}
                </Stack>
              )}
            </FormControl>
          )}

        {/* Restricted damages (view only) */}
        {displayProps.restrictedIds &&
          (ArrayHelper.isNotEmpty(values?.restrictedIds) ||
            ArrayHelper.isNotEmpty(values?._restrictedIdsMemory)) && (
            <FormControl margin='dense' fullWidth>
              <ToggleButtonGroup
                sx={{ mb: 1 }}
                exclusive
                color='standard'
                size='small'
                value={ArrayHelper.isEmpty(values?.restrictedIds) ? "all" : "restricted"}
              >
                <ToggleButton value='all' disabled>
                  All damages
                </ToggleButton>
                <ToggleButton value='restricted'>
                  Restricted damages
                  <AppTooltip title='Damages restricted by the source operation or the current context.'>
                    <AppIcon sx={{ ml: 0.5 }} of='info' />
                  </AppTooltip>
                </ToggleButton>
              </ToggleButtonGroup>

              {ArrayHelper.isNotEmpty(values?.restrictedIds) && (
                <Stack direction='column' gap={0}>
                  {values?.restrictedIds?.map((id) => (
                    <VehicleDamageLink
                      key={id}
                      vehicleId={searchFilters.vehicleId}
                      entityId={id}
                      entity={undefined}
                    />
                  ))}
                </Stack>
              )}
            </FormControl>
          )}
      </Box>

      <AppPopoverActions
        sx={{ mt: 2 }}
        primaryActions={
          <>
            <AppButton variant='outlined' color='text' onClick={cancel}>
              Cancel
            </AppButton>
            <AppButton variant='contained' color='primary' onClick={save}>
              Save
            </AppButton>
          </>
        }
        secondaryActions={
          <>
            <AppButton variant='text' color='text' onClick={reset}>
              Reset
            </AppButton>
          </>
        }
      />
    </Box>
  );
}
